import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BbIcon, WalletValue, walletAmount, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Plus, Bot, History, Activity, ChevronLeft, ChevronRight, Zap, PiggyBank } from 'lucide-react';
import { useChatLayout } from '../context/ChatLayoutContext';
import { DynamicCasePreview } from '../components/CasePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Kısaltmalı format (1.37K, milyon+ → 1,000,000). Intl'in "compact" desteğine GÜVENMEZ
// (bazı tarayıcı/webview/React Native ortamları bunu yok sayıp düz sayı gösterir).
// Tamamen elle hesaplanır, böylece her ortamda K/M/B çalışır.
// API string ("1366.68") veya virgüllü string ("1,366.68") dönse bile güvenli.
const formatCompact = (number) => {
  const raw = typeof number === 'string' ? parseFloat(number.replace(/,/g, '')) : Number(number);
  if (!raw || isNaN(raw)) return "0";
  const sign = raw < 0 ? "-" : "";
  const abs = Math.abs(raw);
  const trim = (v) => v.toFixed(2).replace(/\.?0+$/, ""); // sondaki gereksiz sıfırları at (1.50 -> 1.5)
  if (abs >= 1000000) return sign + abs.toLocaleString();
  if (abs >= 1e3) return sign + trim(abs / 1e3) + "K";
  return sign + trim(abs);
};

// Her oyuncu slotu için sabit renk paleti (elmas ızgarası)
const SLOT_COLORS = ['#22d3ee', '#d946ef', '#22c55e', '#ef4444', '#eab308', '#a855f7'];

// ==========================================
// DYNAMIC CASE PREVIEW COMPONENT (Performans İçin Memoized)
// ==========================================
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı: ALTI dosyada altı kopya zamanla
// ayrışıyor ve her düzeltme altı yere dokunmayı gerektiriyordu.
// ==========================================
// ELMAS OYUNCU IZGARASI (Diamond / Baklava dizilimi)
// ==========================================
const PlayerDiamondGrid = React.memo(({ slots = [] }) => {
  const n = slots.length;
  // Oyuncu sayısına göre sütun: 1-2 -> 2, 3-4 -> 2, 5-6 -> 3
  const cols = n <= 4 ? 2 : 3;

  return (
    <div className="relative w-[104px] h-[88px] flex items-center justify-center shrink-0">
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, transform: 'rotate(45deg)' }}
      >
        {slots.map((slot, i) => {
          const color = SLOT_COLORS[i % SLOT_COLORS.length];
          const filled = !!slot.user_id;
          const hasAvatar = filled && !slot.is_bot && (slot.has_roblox_account || slot.roblox_username);

          return (
            <div
              key={i}
              title={slot.username || 'Empty slot'}
              className={`w-6 h-6 md:w-7 md:h-7 rounded-[5px] flex items-center justify-center overflow-hidden border ${filled ? '' : 'border-dashed'}`}
              style={{
                backgroundColor: filled ? `${color}26` : '#15171a',
                borderColor: filled ? color : '#2A2D31',
                boxShadow: filled ? `0 0 8px ${color}66` : 'none',
              }}
            >
              {hasAvatar ? (
                <img
                  src={`${BACKEND_URL}/api/user/avatar/${slot.username}`}
                  alt="avatar"
                  className="w-full h-full object-cover"
                  style={{ transform: 'rotate(-45deg) scale(1.5)' }}
                  onError={(e) => { e.target.onerror = null; e.target.src = '/default-avatar.png'; }}
                />
              ) : (
                <div className="flex items-center justify-center" style={{ transform: 'rotate(-45deg)' }}>
                  {slot.is_bot ? (
                    <Bot className="w-3.5 h-3.5" style={{ color }} />
                  ) : filled ? (
                    <span className="text-[11px] font-black" style={{ color }}>
                      {slot.username?.charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-[11px] font-black text-zinc-600">?</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ==========================================
// MOD ETİKETİ (VIEW butonu altında: CRAZY / SHARED / ...)
// ==========================================
const ModeTag = ({ battle }) => {
  // CEKIRDEK MOD (oyuncu sayisi/turu: 1v1, 2v2, ...) — DAIMA ve ILK gosterilir.
  // Backend regex ile garanti; yine de eski/eksik veriye karsi guvenli fallback.
  const coreMode = battle.mode || (battle.slots?.length ? `${battle.slots.length}P` : '—');

  // Varyant / modifier'lar — cekirdek modun ARDINDAN gelir.
  const tags = [];
  if (battle.is_terminal_mode) tags.push({ key: 'terminal', label: 'Terminal', cls: 'text-cyan-400' });
  if (battle.is_crazy_mode)    tags.push({ key: 'crazy',    label: 'Crazy',    cls: 'text-rose-400' });
  if (battle.game_mode === 'shared')            tags.push({ key: 'shared',  label: 'Shared',       cls: 'text-amber-400' });
  else if (battle.game_mode === 'biggest_pull') tags.push({ key: 'bp',      label: 'Biggest Pull', cls: 'text-[#c98bf0]' });
  else if (battle.game_mode === 'jackpot')      tags.push({ key: 'jackpot', label: 'Jackpot',      cls: 'text-amber-500' });

  return (
    <>
      {/* DAIMA ILK: oyuncu sayisi/turu (1v1, 2v2, ...) — sitenin mor battles aksanli onceligi */}
      <span
        title={`Mode: ${coreMode}`}
        className="px-1.5 py-0.5 rounded-md bg-[#B059E8]/10 border border-[#B059E8]/30 text-[#c98bf0] text-[11px] font-black uppercase tracking-widest leading-none whitespace-nowrap"
      >
        {coreMode}
      </span>
      {tags.map(t => (
        <span key={t.key} className={`text-[11px] font-black uppercase tracking-widest whitespace-nowrap leading-tight ${t.cls}`}>{t.label}</span>
      ))}
    </>
  );
};

// ==========================================
// BATTLE KARTI (Yatay satır - fotoğraftaki düzen)
// ==========================================
const BattleCard = React.memo(({ battle, isFinished }) => {
  const navigate = useNavigate();
  // 👁️ Tüm lobiler görünür ama yalnızca AYNI cüzdandakine katılabilir.
  // Farklı cüzdandaki lobi soluk gösterilir: kullanıcı tıklamadan önce
  // katılamayacağını anlar, 409 sürprizi yaşamaz.
  const { wallet: myWallet } = useAuth();
  const lobbyWallet = battle.wallet === 'lc' ? 'lc' : 'main';
  const joinable = lobbyWallet === myWallet;

  const totalRounds = battle.cases?.length || 0;
  // 🐷 BORROW: gösterilecek GERÇEK fiyat = effective_cost (ödenecek). Borrowsuz/eski maçlarda cost.
  const shownPrice = (battle.effective_cost != null) ? battle.effective_cost : battle.cost;
  const isWaiting = battle.status === 'waiting' && !isFinished;
  const isRunning = !isFinished && (battle.status === 'playing' || battle.status === 'starting');
  const currentRound = typeof battle.current_round === 'number' ? battle.current_round : -1;

  // Kasa şeridi: aktif kasayı otomatik ortalayan YUMUŞAK kaydırma.
  // 📱 Eskiden `overflow-hidden` + translateX kullanılıyordu; bu, mobilde ekrana sığmayan
  //    kasaların ERİŞİLEMEZ olması demekti (kullanıcı kaydıramıyordu). Artık gerçek yatay
  //    kaydırma var: otomatik ortalama aynen çalışır, ayrıca parmakla gezilebilir.
  const casesRef = useRef(null);
  const stripRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => {
    const recenter = () => {
      const container = casesRef.current;
      const el = activeRef.current;
      if (!container) return;
      if (!el) { container.scrollTo({ left: 0, behavior: 'smooth' }); return; } // aktif kasa yok -> başa
      // getBoundingClientRect ile hesap: offsetParent'tan bağımsız, her düzende doğru.
      const c = container.getBoundingClientRect();
      const e = el.getBoundingClientRect();
      const delta = (e.left + e.width / 2) - (c.left + c.width / 2);
      container.scrollTo({ left: Math.max(0, container.scrollLeft + delta), behavior: 'smooth' });
    };
    recenter();
    window.addEventListener('resize', recenter);
    return () => window.removeEventListener('resize', recenter);
  }, [currentRound, isRunning, battle.cases?.length]);

  // Durum görünümü
  let statusLabel = 'Running';
  let statusColor = '#B059E8';
  if (isFinished || battle.status === 'finished') {
    statusLabel = 'Ended';
    statusColor = '#71717a';
  } else if (isWaiting) {
    statusLabel = 'Waiting';
    statusColor = '#f59e0b';
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, y: 20 }}
      animate={{ opacity: 1, height: 'auto', y: 0 }}
      exit={{ opacity: 0, height: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className={`w-full rounded-2xl border overflow-hidden ${isFinished ? 'bg-[#1A1D20]/50 border-[#2A2D31]/50' : 'bg-[#1A1D20] border-[#2A2D31] hover:border-[#B059E8]/40'} p-2.5 sm:p-3 lg:p-4 flex flex-wrap lg:flex-nowrap items-center gap-x-3 gap-y-2.5 lg:gap-4 shadow-lg`}
    >
      {/* 📱 MOBİL DİZİLİM (order + flex-wrap ile, ayrı bir mobil kopya YOK):
             1. satır → [tur+durum] ....... [fiyat]
             2. satır → [kasa şeridi (tam genişlik)]
             3. satır → [oyuncular] ....... [Join/View + mod etiketleri]
          💻 lg+ → tek satır; fiyat ile kasa şeridi order ile takas edilerek ESKİ masaüstü
                 sırası birebir korunur: [tur] [kasalar] [fiyat] [oyuncular] [buton]. */}

      {/* 1) TUR SAYISI + DURUM */}
      <div className="order-1 flex flex-row lg:flex-col items-center gap-2 lg:gap-0 w-auto lg:w-[84px] shrink-0">
        <div className="relative w-11 h-11 lg:w-14 lg:h-14 rounded-xl bg-[#15171a] border border-[#2A2D31] flex items-center justify-center shrink-0">
          <span
            className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: statusColor, boxShadow: `0 0 8px ${statusColor}` }}
          />
          <span className="text-xl lg:text-2xl font-black" style={{ color: isWaiting ? '#a1a1aa' : '#c98bf0' }}>
            {totalRounds}
          </span>
        </div>
        <span className="mt-0 lg:mt-2 text-[11px] lg:text-xs font-bold uppercase tracking-wide whitespace-nowrap" style={{ color: statusColor }}>
          {statusLabel}
        </span>
      </div>

      {/* 3) FİYAT — 🐷 BORROW: gerçekte ödenecek (effective) fiyat gösterilir
             (mobilde 1. satırın sağına, masaüstünde kasa şeridinden sonra) */}
      <div className="order-2 lg:order-3 flex items-center gap-1.5 shrink-0 w-auto lg:w-[130px] ml-auto lg:ml-0 justify-end lg:justify-center text-base lg:text-lg font-black text-white mm-num">
        {/* Cüzdan rozeti — farklı cüzdandaki lobi soluk. */}
        {!isFinished && !joinable && (
          <span className="absolute top-2 right-2 z-20 flex items-center gap-1 text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-black/60 text-zinc-400 tracking-wider">
            {lobbyWallet === 'lc' ? <BbIcon size={11} /> : 'ROBUX'}
            <span>only</span>
          </span>
        )}
        {/* ⚠️ FİYAT LOBİNİN KENDİ CÜZDANINDA. Backend her lobiye `wallet`
            mühürlüyor ve tutarları o birimde gönderiyor. Sabit "$" yazmak
            1.700.000 mili-LC'lik ($1.70) bir girişi "1.7M$" gösterirdi. */}
        <WalletValue amount={shownPrice} wallet={battle.wallet} />
      </div>

      {/* 2) KASALAR + "ŞU ANKİ KASA" İŞARETÇİSİ (aktif kasayı yumuşakça ortalar, parmakla da gezilir) */}
      <div ref={casesRef} className="order-3 lg:order-2 w-full lg:flex-1 min-w-0 rounded-xl bg-[#101214] border border-[#2A2D31]/70 px-2 py-2 lg:px-3 lg:py-3 overflow-x-auto cb-scroll">
        <div
          ref={stripRef}
          className="relative flex items-center gap-2 md:gap-3 min-h-[56px] lg:min-h-[64px] w-max"
        >
          {battle.cases?.map((c, i) => {
            const isCurrent = isRunning && currentRound === i;
            const colorCase = c.image_url?.startsWith('color:');
            return (
              <div
                key={i}
                ref={isCurrent ? activeRef : null}
                title={c.name}
                className={`relative shrink-0 w-14 h-14 lg:w-16 lg:h-16 rounded-lg flex items-center justify-center transition-all ${isCurrent ? 'bg-[#B059E8]/10 ring-1 ring-[#B059E8]/40' : ''}`}
              >
                {/* Üstte aşağı bakan ▼, altta yukarı bakan ▲ üçgenler */}
                {isCurrent && (
                  <>
                    <div
                      className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-0 h-0"
                      style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #B059E8' }}
                    />
                    <div
                      className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-0 h-0"
                      style={{ borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: '8px solid #B059E8' }}
                    />
                  </>
                )}

                {colorCase ? (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none transform scale-[0.36] lg:scale-[0.4]">
                    <DynamicCasePreview caseColor={c.image_url} items={c.items || []} showcase={c.showcase} />
                  </div>
                ) : (
                  <img
                    src={c.image_url}
                    alt={c.name}
                    draggable={false}
                    className={`w-11 h-11 lg:w-12 lg:h-12 object-contain drop-shadow-md transition-opacity ${isRunning && !isCurrent ? 'opacity-40' : 'opacity-100'}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 4) ELMAS OYUNCU IZGARASI */}
      <div className="order-4 shrink-0">
        <PlayerDiamondGrid slots={battle.slots || []} />
      </div>

      {/* 5) VIEW + MOD ETİKETİ */}
      <div className="order-5 flex flex-col items-center gap-1 flex-1 lg:flex-none w-auto lg:w-[110px] min-w-0">
        <button
          onClick={() => navigate(`/battles/${battle.id}`)}
          className={`w-full px-4 lg:px-6 py-3.5 lg:py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${
            isWaiting
              ? 'bg-gradient-to-r from-[#16A34A] to-[#15913f] hover:from-[#1bbe57] hover:to-[#16A34A] text-white shadow-[0_0_14px_rgba(22,163,74,0.45)]'
              : isFinished
                ? 'bg-[#2A2D31]/40 hover:bg-[#33363b]/60 text-zinc-300 border border-[#2A2D31]'
                : 'bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] text-white shadow-[0_0_14px_rgba(176,89,232,0.4)]'
          }`}
        >
          {isWaiting ? 'Join' : 'View'}
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0 max-w-full">
          <ModeTag battle={battle} />
          {battle.fast_mode && (
            <span className="text-[11px] font-black uppercase tracking-widest text-amber-400 inline-flex items-center gap-0.5 whitespace-nowrap leading-tight">
              <Zap className="w-3 h-3" /> Fast
            </span>
          )}
          {battle.borrow_percent > 0 && (
            <span
              title={`Borrow ${battle.borrow_percent}% — cheaper entry, smaller payout`}
              className="text-[11px] font-black uppercase tracking-widest text-pink-400 inline-flex items-center gap-0.5 whitespace-nowrap leading-tight"
            >
              <PiggyBank className="w-3 h-3" /> {battle.borrow_percent}%
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
});

export const CaseBattles = () => {
  const [activeBattles, setActiveBattles] = useState([]);
  const [finishedBattles, setFinishedBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('price'); // 'price' | 'date'
  const { wallet } = useAuth();
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();

  useEffect(() => {
    const fetchBattles = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/game/battles`);
        setActiveBattles(res.data.active || []);
        setFinishedBattles(res.data.finished || []);
      } catch (error) {
        console.error("Battles fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBattles();
    const interval = setInterval(fetchBattles, 3000);
    // 🍀 Liste AKTİF CÜZDANA göre gelir (backend filtreliyor): kullanıcı
    // katılamayacağı lobileri görmez. Cüzdan değişince ANINDA tazeliyoruz;
    // yoksa 3 saniyeye kadar diğer cüzdanın lobileri ekranda kalır ve
    // tıklayan kullanıcı 409 alır.
    const onWalletChange = () => fetchBattles();
    window.addEventListener('wallet:changed', onWalletChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('wallet:changed', onWalletChange);
    };
  }, [wallet]);

  // Sıralama (Price / Date)
  const sortedActive = [...activeBattles].sort((a, b) => {
    // 🐷 BORROW: sıralama da gösterilen (effective) fiyata göre — tutarlı görünüm
    if (sortBy === 'price') {
      const ap = (a.effective_cost != null) ? a.effective_cost : (a.cost || 0);
      const bp = (b.effective_cost != null) ? b.effective_cost : (b.cost || 0);
      return bp - ap;
    }
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  // Header sayaçları
  const activeCount = activeBattles.length;
  const joinableCount = activeBattles.filter(
    (b) => b.status === 'waiting' && (b.slots || []).some((s) => !s.user_id)
  ).length;

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-20 sm:pt-24 pb-12">
      {/* Yatay kaydırma çubuğunu gizle (kaydırma çalışmaya devam eder) — kasa şeridi için */}
      <style>{`
        .cb-scroll { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .cb-scroll::-webkit-scrollbar { height: 0; width: 0; }
      `}</style>
      <div className={`max-w-[1800px] mx-auto px-3 sm:px-4 flex gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
        <main className="flex-1 min-w-0">
          {/* HEADER BAR (fotoğraftaki gibi: solda sıralama, sağda sayaçlar + Create)
              📱 Mobilde 2 satır: [Battles ⚔ ..... price|date] / [sayaçlar ..... Create] */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 lg:gap-4 mb-5 lg:mb-6">
            {/* Sol: başlık + sıralama */}
            <div className="flex items-center gap-3 lg:gap-4">
              <h1 className="font-heading font-black text-xl sm:text-2xl md:text-3xl text-white uppercase tracking-wider flex items-center gap-2 shrink-0">
                <Swords className="w-6 h-6 sm:w-7 sm:h-7 text-[#B059E8]" /> Battles
              </h1>
              <div className="flex items-center gap-1.5 p-1 bg-[#1A1D20] rounded-xl border border-[#2A2D31] ml-auto lg:ml-0 shrink-0">
                {['price', 'date'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-bold uppercase tracking-widest transition-all ${
                      sortBy === s
                        ? 'bg-[#2A2D31] text-white border border-[#B059E8]/40'
                        : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Sağ: sayaçlar + Create Battle */}
            <div className="flex items-center justify-between lg:justify-start gap-3 lg:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 text-[11px] sm:text-sm font-bold min-w-0">
                <span className="text-zinc-200 whitespace-nowrap">
                  {activeCount} <span className="text-zinc-500 font-medium">Active<span className="hidden sm:inline"> battles</span></span>
                </span>
                <span className="text-[#2A2D31]">|</span>
                <span className="text-[#2ecf6e] whitespace-nowrap">
                  {joinableCount} <span className="text-[#2ecf6e]/70 font-medium">Joinable<span className="hidden sm:inline"> battles</span></span>
                </span>
              </div>
              <Link
                to="/battles/create"
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] text-white rounded-xl font-black uppercase text-xs sm:text-sm tracking-widest shadow-[0_0_20px_rgba(176,89,232,0.35)] flex items-center justify-center gap-1.5 sm:gap-2 transition-transform hover:scale-105 shrink-0 whitespace-nowrap"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> Create<span className="hidden sm:inline"> Battle</span>
              </Link>
            </div>
          </div>

          {/* AKTİF SAVAŞLAR */}
          <div className="space-y-3 sm:space-y-4 mb-10 sm:mb-12">
            {loading ? (
              <div className="py-12 flex justify-center"><Activity className="w-8 h-8 text-[#B059E8] animate-spin" /></div>
            ) : sortedActive.length > 0 ? (
              <AnimatePresence initial={false}>
                {sortedActive.map(b => <BattleCard key={b.id} battle={b} isFinished={false} />)}
              </AnimatePresence>
            ) : (
              <div className="bg-[#1A1D20]/50 border border-[#2A2D31]/50 rounded-2xl py-10 sm:py-12 flex flex-col items-center justify-center text-zinc-500 px-4 text-center">
                <Swords className="w-10 h-10 sm:w-12 sm:h-12 mb-3 opacity-20" />
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest">No active battles found</p>
                <p className="text-xs mt-1">Be the first to create one!</p>
              </div>
            )}
          </div>

          {/* BİTEN SAVAŞLAR */}
          <div className="flex items-center gap-3 mb-5 sm:mb-6 border-b border-[#2A2D31] pb-4">
            <History className="w-5 h-5 text-zinc-400" />
            <h2 className="font-heading font-bold text-lg sm:text-xl text-white uppercase tracking-wide">Latest Results</h2>
          </div>
          {/* Dokunmatik cihazlarda :hover yok → mobilde sönük kalmasın diye opaklık lg'den itibaren */}
          <div className="space-y-3 sm:space-y-4 lg:opacity-70 lg:hover:opacity-100 transition-opacity">
            <AnimatePresence initial={false}>
              {finishedBattles.map(b => <BattleCard key={b.id} battle={b} isFinished={true} />)}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
};